import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { rgba } from "polished";

import {
  Box,
  Card as MuiCard,
  CardContent as MuiCardContent,
  CardHeader,
  Chip as MuiChip,
  Typography as MuiTypography,
  Button as MuiButton,
  CircularProgress,
} from "@mui/material";
import { Loop as LoopIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";
import { getStatsData } from "src/services/generalService";

const illustrationCardStyle = (props) => css`
  ${props.illustration &&
  props.theme.palette.mode !== "dark" &&
  `
    background: ${rgba(props.theme.palette.primary.main, 0.125)};
    color: ${props.theme.palette.primary.main};
  `}
`;

const Card = styled(MuiCard)`
  position: relative;
  margin-bottom: ${(props) => props.theme.spacing(6)};

  ${illustrationCardStyle}
`;

const Typography = styled(MuiTypography)(spacing);

const CardContent = styled(MuiCardContent)`
  position: relative;

  &:last-child {
    padding-bottom: ${(props) => props.theme.spacing(4)};
  }
`;

const Chip = styled(MuiChip)`
  position: absolute;
  top: 16px;
  right: 16px;
  height: 20px;
  padding: 4px 0;
  font-size: 85%;
  background-color: ${(props) => props.theme.palette.secondary.main};
  color: ${(props) => props.theme.palette.common.white};
  margin-bottom: ${(props) => props.theme.spacing(4)};

  span {
    padding-left: ${(props) => props.theme.spacing(2)};
    padding-right: ${(props) => props.theme.spacing(2)};
  }
`;

const SmallButton = styled(MuiButton)`
  padding: 4px;
  min-width: 0;

  svg {
    width: 0.9em;
    height: 0.9em;
  }
`;

const illustrationPercentageStyle = (props) => css`
  ${props.illustration &&
  props.theme.palette.mode !== "dark" &&
  `
    color: ${rgba(props.theme.palette.primary.main, 0.85)};
  `}
`;

const Percentage = styled(MuiTypography)`
  span {
    color: ${(props) => props.percentagecolor};
    font-weight: ${(props) => props.theme.typography.fontWeightBold};
    background: ${(props) => rgba(props.percentagecolor, 0.1)};
    padding: 2px;
    border-radius: 3px;
    margin-right: ${(props) => props.theme.spacing(2)};
  }

  ${illustrationPercentageStyle}
`;

const IllustrationImage = styled.img`
  height: 100px;
  position: absolute;
  right: ${(props) => props.theme.spacing(1)};
  bottom: ${(props) => props.theme.spacing(1)};
  display: none;

  ${(props) => props.theme.breakpoints.between("xs", "lg")} {
    display: block;
  }

  @media (min-width: 1600px) {
    display: block;
  }
`;

const LoaderWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
`;

/**
 * Reusable Stats Card component that fetches data from an API
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Card title
 * @param {string} props.apiUrl - API endpoint to fetch data from
 */
const StatsCard = ({
  title,
  apiUrl,
}) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getStatsData(apiUrl);

      if (response?.status !== 200 || !response?.data) {
        throw new Error(
          response?.message || `API Error: ${response?.status || "Unknown"}`
        );
      }

      const data = response?.data;

      setStats({
        amount: data.amount,
        percentagetext: data.percentagetext,
        percentagecolor: data.percentagecolor,
        chip: data.chip,
        illustration: data.illustration,
      });
    } catch (err) {
      console.error("Error fetching stats data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  useEffect(() => {
    if (apiUrl) {
      fetchData();
    }
  }, [apiUrl]);

  if (loading) {
    return (
      <Card illustration={false}>
        <CardHeader
          action={
            <SmallButton
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <LoopIcon />
            </SmallButton>
          }
          title={title}
        />
        <CardContent>
          <LoaderWrapper>
            <CircularProgress />
          </LoaderWrapper>
        </CardContent>
      </Card>
    );
  }

  if (error || !stats) {
    return (
      <Card illustration={false}>
        <CardHeader
          action={
            <SmallButton
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <LoopIcon />
            </SmallButton>
          }
          title={title}
        />
        <CardContent>
          <LoaderWrapper>
            <div>Error loading data</div>
          </LoaderWrapper>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card illustration={stats.illustration}>
      <CardHeader
        action={
          <SmallButton
            size="small"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <LoopIcon />
          </SmallButton>
        }
        title={title}
      />
      <CardContent>
        {/* <Typography variant="h6" mb={4}>
          {title}
        </Typography> */}
        <Typography variant="h3" mb={3}>
          <Box fontWeight="fontWeightRegular">{stats.amount}</Box>
        </Typography>
        <Percentage
          variant="subtitle2"
          color="textSecondary"
          percentagecolor={stats.percentagecolor}
          illustration={stats.illustration}
        >
          <span>{stats.percentagetext}</span> Since last month
        </Percentage>
        {/* {!stats.illustration && <Chip label={stats.chip} />} */}
      </CardContent>

      {!!stats.illustration && (
        <IllustrationImage src={stats.illustration} alt="Illustration" />
      )}
    </Card>
  );
};

export default StatsCard;
